import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, orgTypeHomePath, orgTypeToFrontendRole } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import { PoliceDashboardProvider } from "@/providers/PoliceDashboardProvider";
import { SocketProvider } from "@/providers/SocketProvider";
import { AlertProvider } from "@/hooks/useAlert";
import EmergencyAlertModal from "@/components/shared/EmergencyAlertModal";

import LoginPage from "@/pages/auth/Login";
import SignupPage from "@/pages/auth/Signup";
import OTPVerification from "@/pages/auth/OTPVerification";
import ForgotPassword from "@/pages/auth/ForgotPassword";

import DashboardLayout from "@/components/shared/DashboardLayout";

import OnboardCustomerModal from "@/pages/OnboardCustomerPage";

// Super Admin
import AdminDashboard from "@/pages/superadmin/Dashboard";
import LiveMonitoring from "@/pages/superadmin/LiveMonitoring";
import UsersPage from "@/pages/superadmin/UsersPage";
import VehiclesPage from "@/pages/superadmin/VehiclesPage";
import VehicleActivities from "@/pages/superadmin/VehicleActivities";
import DevicesPage from "@/pages/superadmin/DevicesPage";
import AlertsPage from "@/pages/superadmin/AlertsPage";
import IncidentsPage from "@/pages/superadmin/IncidentsPage";
import ServicesManagement from "@/pages/superadmin/ServicesManagement";
import AnalyticsPage from "@/pages/superadmin/AnalyticsPage";
import CustomerPage from "@/pages/superadmin/CustomerPage";
import SettingsPage from "@/pages/superadmin/SettingsPage";
import SecurityLogsPage from "@/pages/superadmin/SecurityLogsPage";
import ServiceProviderDetails from "./pages/ServiceProviderDetails";

// Police
import PoliceDashboard from "@/pages/police/Dashboard";
import PoliceNewAlerts from "@/pages/police/NewAlerts";
import PoliceActiveCases from "@/pages/police/ActiveCases";
import PoliceCompletedCases from "@/pages/police/CompletedCases";
import PoliceReports from "@/pages/police/Reports";
import PoliceLiveMonitoring from "@/pages/police/LiveMonitoring";
import PoliceVehicleActivities from "@/pages/police/VehicleActivities";

// Ambulance
import AmbulanceDashboard from "@/pages/ambulance/Dashboard";
import AmbulanceIncoming from "@/pages/ambulance/IncomingAlerts";
import AmbulanceActive from "@/pages/ambulance/ActiveTrips";
import AmbulanceCompleted from "@/pages/ambulance/CompletedTrips";

// Fire
import FireDashboard from "@/pages/fire/Dashboard";
import FireAlerts from "@/pages/fire/NewAlerts";
import FireActive from "@/pages/fire/ActiveIncidents";
import FireReports from "@/pages/fire/Reports";

// Hospital
import HospitalIncoming from "@/pages/hospital/IncomingPatients";
import HospitalBeds from "@/pages/hospital/BedManagement";
import HospitalActive from "@/pages/hospital/ActiveCases";

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? orgTypeHomePath(user.org_type) : "/login"} replace />;
}

function ProtectedRoute({ requiredOrgType, element }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  // `user.org_type` comes from backend (e.g. "PLATFORM") while routes use frontend roles
  // (e.g. "super_admin"). Map backend org_type to frontend role before comparison.
  const frontendRole = user?.frontendRole || orgTypeToFrontendRole(user?.org_type);
  if (requiredOrgType && frontendRole !== requiredOrgType) return <Navigate to={orgTypeHomePath(user?.org_type)} replace />;
  return element;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/otp" element={<OTPVerification />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/onboard-customer" element={<OnboardCustomerModal />} />

      {/* Super Admin */}
      <Route path="/admin" element={<ProtectedRoute requiredOrgType="super_admin" element={<DashboardLayout role="super_admin" />} />}>
        <Route index element={<AdminDashboard />} />
        <Route path="live" element={<LiveMonitoring />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:userId" element={<UsersPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="vehicles/:vehicleId" element={<VehiclesPage />} />
        <Route path="activities" element={<VehicleActivities />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="services" element={<ServicesManagement />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="customers" element={<CustomerPage />} />
        <Route path="service-provider-details" element={<ServiceProviderDetails />} />

        <Route path="service-provider-details/:organizationId" element={<ServiceProviderDetails />} />



        <Route path="settings" element={<SettingsPage />} />
        <Route path="security" element={<SecurityLogsPage />} />
      </Route>

      {/* Police */}
      <Route path="/police" element={<ProtectedRoute requiredOrgType="police" element={<PoliceDashboardProvider><DashboardLayout role="police" /></PoliceDashboardProvider>} />}>
        <Route index element={<PoliceDashboard />} />
        <Route path="alerts" element={<PoliceNewAlerts />} />
        <Route path="live-monitoring" element={<PoliceLiveMonitoring />} />
        <Route path="active" element={<PoliceActiveCases />} />
        <Route path="completed" element={<PoliceCompletedCases />} />
        <Route path="activities" element={<PoliceVehicleActivities />} />
        <Route path="reports" element={<PoliceReports />} />
        <Route path="service-provider-details" element={<ServiceProviderDetails />} />
      </Route>

      {/* Ambulance */}
      <Route path="/ambulance" element={<ProtectedRoute requiredOrgType="ambulance" element={<DashboardLayout role="ambulance" />} />}>
        <Route index element={<AmbulanceDashboard />} />
        <Route path="alerts" element={<AmbulanceIncoming />} />
        <Route path="active" element={<AmbulanceActive />} />
        <Route path="completed" element={<AmbulanceCompleted />} />
        <Route path="service-provider-details" element={<ServiceProviderDetails />} />
      </Route>

      {/* Fire */}
      <Route path="/fire" element={<ProtectedRoute requiredOrgType="fire" element={<DashboardLayout role="fire" />} />}>
        <Route index element={<FireDashboard />} />
        <Route path="alerts" element={<FireAlerts />} />
        <Route path="active" element={<FireActive />} />
        <Route path="reports" element={<FireReports />} />
        <Route path="service-provider-details" element={<ServiceProviderDetails />} />
      </Route>

      {/* Hospital */}
      <Route path="/hospital" element={<DashboardLayout role="hospital" />}>
        <Route index element={<HospitalIncoming />} />
        <Route path="beds" element={<HospitalBeds />} />
        <Route path="active" element={<HospitalActive />} />
        <Route path="service-provider-details" element={<ServiceProviderDetails />} />
      </Route>

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function AppContent() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <EmergencyAlertModal />
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

function App() {
  useEffect(() => {
    document.title = "Reyansh Technologies";
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <SocketProvider>
          <AlertProvider>
            <AppContent />
          </AlertProvider>
        </SocketProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
